/**
 * Charidy Sign-In "host bridge", v1: the dashboard (parent) side, for the custom
 * report frame. Port of dashboard-v2 `app/utils/report-host-bridge.ts`; the
 * contract is ssr2 `docs/auth/protocol.md` ("Host bridge").
 *
 * A custom report renders in a sandboxed `srcdoc` frame with an opaque origin.
 * When it refreshes its data it asks this page to run the request with the
 * dashboard's own session and gets back only the answer. No token ever crosses
 * into the frame.
 *
 * Messages:
 *   child -> parent  { type: "charidy-auth:hello", v: 1, id }
 *   parent -> child  { type: "charidy-auth:hello", v: 1, id, host: "dashboard", user, org_id }
 *   child -> parent  { type: "charidy-auth:fetch", v: 1, id, url, method: "GET" }
 *   parent -> child  { type: "charidy-auth:fetch-result", v: 1, id, status, contentType, body }
 *                  | { type: "charidy-auth:fetch-result", v: 1, id, error: "not_allowed" | "network" }
 *
 * Reports the builder wrote from 2026-10-10 speak an older protocol: they post
 * `{ type: "charidy:report:auth-request" }` and call the API with the `token`
 * of the `charidy:report:auth` answer. That answer used to carry the login JWT;
 * it now carries LEGACY_REPORT_AUTH_TOKEN, and the frame shim
 * (`report-frame-shim.js`) sends those reports' refresh through the bridge.
 */

export const REPORT_BRIDGE_VERSION = 1;
export const REPORT_BRIDGE_HELLO = "charidy-auth:hello";
export const REPORT_BRIDGE_FETCH = "charidy-auth:fetch";
export const REPORT_BRIDGE_FETCH_RESULT = "charidy-auth:fetch-result";

export const LEGACY_REPORT_AUTH_REQUEST = "charidy:report:auth-request";
export const LEGACY_REPORT_AUTH = "charidy:report:auth";

/**
 * Stands where the login JWT used to be. Not a credential: the API refuses it,
 * so a request that slips past the frame shim fails instead of reaching data.
 */
export const LEGACY_REPORT_AUTH_TOKEN = "charidy-host-bridge";

/**
 * The report refresh endpoint, `GET {api}/api/v1/reports/{token}`. Production
 * serves it without the `/c`, local runs with it. Report tokens are
 * `{uuid}_{hash}`, so the token is held to URL-safe characters.
 */
const REPORT_REFRESH_PATH = /^(?:\/c)?\/api\/v1\/reports\/[\w-]{1,256}$/;

const MAX_MESSAGE_ID_LENGTH = 128;

/** The origin of a sandboxed frame without `allow-same-origin`, the only frame the bridge serves. */
const OPAQUE_ORIGIN = "null";

/** A rate-limited request gets the answer a rate-limiting proxy would give. */
const TOO_MANY_REQUESTS = {
  status: 429,
  contentType: "application/json",
  body: JSON.stringify({ error: "too_many_requests" }),
};

/**
 * A report's refresh fires one request per query it shows, all at once; a
 * report that loops on refresh is held to about one request a second.
 */
export const DEFAULT_REPORT_BRIDGE_LIMITS = {
  maxInFlight: 4,
  maxQueued: 32,
  maxPerWindow: 60,
  windowMs: 60000,
};

function isRecord(value) {
  return typeof value === "object" && value !== null;
}

function isMessageId(value) {
  if (typeof value === "string") return value.length > 0 && value.length <= MAX_MESSAGE_ID_LENGTH;
  return typeof value === "number" && Number.isFinite(value);
}

/** Reads a bridge request out of `event.data`, or null when it is not one. */
export function parseReportBridgeRequest(data) {
  if (!isRecord(data) || data.v !== REPORT_BRIDGE_VERSION || !isMessageId(data.id)) return null;

  if (data.type === REPORT_BRIDGE_HELLO) {
    return { type: REPORT_BRIDGE_HELLO, v: REPORT_BRIDGE_VERSION, id: data.id };
  }

  if (data.type === REPORT_BRIDGE_FETCH) {
    return {
      type: REPORT_BRIDGE_FETCH,
      v: REPORT_BRIDGE_VERSION,
      id: data.id,
      url: typeof data.url === "string" ? data.url : "",
      method: typeof data.method === "string" ? data.method : "",
    };
  }

  return null;
}

/** True when `data` is the old protocol's request. */
export function isLegacyReportAuthRequest(data) {
  return isRecord(data) && data.type === LEGACY_REPORT_AUTH_REQUEST;
}

/**
 * The old protocol's answer: the org and hosts the report reads and the
 * placeholder where the login JWT used to be. Built field by field, so nothing
 * else the caller holds can ride along.
 */
export function legacyReportAuthMessage(context) {
  return {
    type: LEGACY_REPORT_AUTH,
    token: LEGACY_REPORT_AUTH_TOKEN,
    orgId: String(context.orgId),
    apiHost: String(context.apiHost),
    dashboardHost: String(context.dashboardHost),
  };
}

/**
 * The URL to request when `requested` is a report refresh on the configured
 * API, or null. Parsed, not matched as a string: same origin as the API, the
 * refresh route with one token, no credentials in the URL. The fragment is
 * dropped; the query is kept.
 */
export function resolveReportRefreshUrl(requested, apiBaseUrl) {
  let target;
  let api;
  try {
    target = new URL(requested);
    api = new URL(apiBaseUrl);
  } catch (e) {
    return null;
  }

  if (api.origin === OPAQUE_ORIGIN || target.origin !== api.origin) return null;
  if (target.username || target.password) return null;
  if (!REPORT_REFRESH_PATH.test(target.pathname)) return null;

  return `${target.origin}${target.pathname}${target.search}`;
}

/**
 * The parent side of the bridge for one report frame. Create one per frame and
 * dispose it when the frame goes away.
 *
 * - Only messages whose `source` is the frame's window and whose origin is
 *   opaque are handled; answers go to that window only, with target origin `*`.
 * - A fetch runs only for GET to the report refresh endpoint on the API;
 *   anything else is answered `not_allowed`.
 * - The request carries the dashboard's session through `fetcher`. The bridge
 *   never sees a token, and an error is answered with its code alone.
 * - The old protocol is answered with the org, the hosts and a placeholder
 *   token, never a credential.
 *
 * @param {object} options
 * @param {() => ({ postMessage: Function } | null)} options.getFrameWindow the report frame's window, or null
 * @param {string} options.apiBaseUrl base URL of the public API; only its origin is allowed
 * @param {() => ({ user: object, org_id: number } | null)} options.getIdentity who is signed in, or null
 * @param {(url: string, signal: AbortSignal) => Promise<{ status: number, contentType: string, body: string }>} options.fetcher
 *   runs an allowed GET with the dashboard's session; rejects only when there is no HTTP answer
 * @param {() => ({ orgId: string, apiHost: string, dashboardHost: string } | null)} [options.getLegacyAuthContext]
 * @param {object} [options.limits]
 * @param {() => number} [options.now]
 */
export function createReportHostBridge(options) {
  const limits = { ...DEFAULT_REPORT_BRIDGE_LIMITS, ...options.limits };
  const now = options.now || Date.now;
  const controller = new AbortController();

  const accepted = [];
  const queue = [];
  let inFlight = 0;
  let disposed = false;

  function reply(target, message) {
    if (disposed) return;
    target.postMessage(message, "*");
  }

  function replyFetch(target, id, result) {
    reply(target, { type: REPORT_BRIDGE_FETCH_RESULT, v: REPORT_BRIDGE_VERSION, id, ...result });
  }

  function takeWindowSlot() {
    const windowStart = now() - limits.windowMs;
    while (accepted.length > 0 && accepted[0] <= windowStart) accepted.shift();
    if (accepted.length >= limits.maxPerWindow) return false;
    accepted.push(now());
    return true;
  }

  function pump() {
    while (!disposed && inFlight < limits.maxInFlight && queue.length > 0) {
      const job = queue.shift();
      inFlight++;
      job().finally(() => {
        inFlight--;
        pump();
      });
    }
  }

  async function run(target, id, url) {
    try {
      const response = await options.fetcher(url, controller.signal);
      replyFetch(target, id, {
        status: response.status,
        contentType: response.contentType,
        body: response.body,
      });
    } catch (e) {
      // The error is not passed on: its message may name the session.
      replyFetch(target, id, { error: "network" });
    }
  }

  function handleFetch(target, id, requestedUrl, method) {
    const url =
      method.toUpperCase() === "GET"
        ? resolveReportRefreshUrl(requestedUrl, options.apiBaseUrl)
        : null;
    if (!url) {
      replyFetch(target, id, { error: "not_allowed" });
      return;
    }

    if (inFlight >= limits.maxInFlight && queue.length >= limits.maxQueued) {
      replyFetch(target, id, TOO_MANY_REQUESTS);
      return;
    }

    if (!takeWindowSlot()) {
      replyFetch(target, id, TOO_MANY_REQUESTS);
      return;
    }

    queue.push(() => run(target, id, url));
    pump();
  }

  function replyLegacyAuth(frame) {
    const context = options.getLegacyAuthContext ? options.getLegacyAuthContext() : null;
    if (context) reply(frame, legacyReportAuthMessage(context));
  }

  function handleMessage(event) {
    if (disposed) return;

    const frame = options.getFrameWindow();
    if (!frame || event.source !== frame || event.origin !== OPAQUE_ORIGIN) return;

    if (isLegacyReportAuthRequest(event.data)) {
      replyLegacyAuth(frame);
      return;
    }

    const request = parseReportBridgeRequest(event.data);
    if (!request) return;

    if (request.type === REPORT_BRIDGE_HELLO) {
      const identity = options.getIdentity();
      if (!identity) return;
      reply(frame, {
        type: REPORT_BRIDGE_HELLO,
        v: REPORT_BRIDGE_VERSION,
        id: request.id,
        host: "dashboard",
        user: {
          login_id: identity.user.login_id,
          first_name: identity.user.first_name,
          last_name: identity.user.last_name,
          email: identity.user.email,
        },
        org_id: identity.org_id,
      });
      return;
    }

    handleFetch(frame, request.id, request.url, request.method);
  }

  /** Posts the old protocol's answer unasked; call it on the frame's `load`. */
  function announceLegacyAuth() {
    if (disposed) return;
    const frame = options.getFrameWindow();
    if (frame) replyLegacyAuth(frame);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    queue.length = 0;
    controller.abort();
  }

  return { handleMessage, announceLegacyAuth, dispose };
}
